import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from '@/components/ui/pagination';
import { useToast } from '@/hooks/use-toast';
import { Search, CheckCircle, XCircle, Clock, Trophy, AlertCircle, Shield, ArrowLeft, Globe, Loader2, Ban } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { LoadingFallback } from '@/components/LoadingFallback';
import { useDebounce } from '@/hooks/useDebounce';
import { ViewToggle, ViewType } from '@/components/marketplace/ViewToggle';
import { AthleteListView } from '@/components/marketplace/AthleteListView';
import { AthleteCompactView } from '@/components/marketplace/AthleteCompactView';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const SPORTS = [
  { value: 'all', label: 'All Sports' },
  { value: 'nba', label: 'NBA' },
  { value: 'nfl', label: 'NFL' },
  { value: 'mlb', label: 'MLB' },
  { value: 'nhl', label: 'NHL' },
  { value: 'wnba', label: 'WNBA' },
  { value: 'mls', label: 'MLS' },
  { value: 'mcbb', label: 'NCAA Basketball (M)' },
  { value: 'wcbb', label: 'NCAA Basketball (W)' },
  { value: 'cfb', label: 'NCAA Football' },
];

const PAGE_SIZE = 50;

// Determine athlete active status from api_data JSON (ESPN API stores status info there)
const getAthleteActiveStatus = (athlete: any): 'active' | 'retired' | 'unknown' => {
  const apiData = athlete?.api_data;
  if (!apiData) {
    // Fallback: if no api_data, use current_team_name as proxy
    return athlete?.current_team_name ? 'active' : 'retired';
  }
  // Check ESPN status field
  if (apiData.status?.name) {
    const statusName = apiData.status.name.toLowerCase();
    if (statusName === 'active') return 'active';
    if (statusName === 'retired' || statusName === 'inactive') return 'retired';
  }
  // Check ESPN active boolean
  if (typeof apiData.active === 'boolean') {
    return apiData.active ? 'active' : 'retired';
  }
  // Fallback to team-based check
  return athlete?.current_team_name ? 'active' : 'retired';
};

// Get the effective claim status — treats orphaned pending claims (no owner) as unclaimed
const getEffectiveClaimStatus = (athlete: any): string => {
  const status = athlete?.claim_status || 'unclaimed';
  // If pending but no one owns it, treat as unclaimed (orphaned from cancelled claim)
  if (status === 'pending' && !athlete?.claimed_by_profile_id) {
    return 'unclaimed';
  }
  return status;
};

// Compute a relevance score for how closely an athlete's name matches the query.
// Higher = better match.  Used when the user types a search so the closest names
// float to the top instead of being buried in alphabetical order.
const computeRelevanceScore = (name: string, query: string): number => {
  const nameLower = name.toLowerCase().trim();
  const queryLower = query.toLowerCase().trim();
  if (!queryLower) return 0;

  // 1. Exact match
  if (nameLower === queryLower) return 100;

  // 2. Name starts with query ("LeBron" matches "lebron")
  if (nameLower.startsWith(queryLower)) return 90;

  const queryParts = queryLower.split(/\s+/).filter(Boolean);
  const nameParts = nameLower.split(/\s+/).filter(Boolean);

  // 3. Multi-word: first & last query words match the start of first & last name parts
  if (queryParts.length >= 2 && nameParts.length >= 2) {
    const firstMatch = nameParts[0].startsWith(queryParts[0]);
    const lastMatch = nameParts[nameParts.length - 1].startsWith(queryParts[queryParts.length - 1]);
    if (firstMatch && lastMatch) return 85;
  }

  // 4. Full query string found as contiguous substring
  if (nameLower.includes(queryLower)) return 75;

  // 5. Every query word starts a word in the name
  let wordStartMatches = 0;
  for (const qp of queryParts) {
    if (nameParts.some(np => np.startsWith(qp))) wordStartMatches++;
  }
  if (wordStartMatches === queryParts.length) return 70;

  // 6. Partial word-start matches
  if (wordStartMatches > 0) return 50 + (wordStartMatches / queryParts.length) * 15;

  // 7. Substring containment per word
  let subMatches = 0;
  for (const qp of queryParts) {
    if (nameParts.some(np => np.includes(qp))) subMatches++;
  }
  if (subMatches > 0) return 30 + (subMatches / queryParts.length) * 15;

  return 10;
};

export const ClaimAthleteProfile: React.FC = () => {
  const { currentUser } = useUser();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSport, setSelectedSport] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [claimStatusFilter, setClaimStatusFilter] = useState('all');
  const [positionFilter, setPositionFilter] = useState('all');
  const [leagueFilter, setLeagueFilter] = useState('all');
  const [conferenceFilter, setConferenceFilter] = useState('all');
  const [divisionFilter, setDivisionFilter] = useState('all');
  const [collegiateFilter, setCollegiateFilter] = useState('all'); // 'all' | 'college' | 'pro'
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'active' | 'retired'
  const [sortBy, setSortBy] = useState('name-asc');
  const [viewType, setViewType] = useState<ViewType>('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedAthlete, setSelectedAthlete] = useState<any>(null);
  const [showClaimDialog, setShowClaimDialog] = useState(false);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [verificationProofUrl, setVerificationProofUrl] = useState('');
  
  // ESPN search state
  const [espnResults, setEspnResults] = useState<any[]>([]);
  const [espnSearching, setEspnSearching] = useState(false);
  const [espnSearched, setEspnSearched] = useState(false);
  const [syncingAthleteId, setSyncingAthleteId] = useState<string | null>(null);
  const [expandedEspnId, setExpandedEspnId] = useState<string | null>(null);

  const debouncedSearch = useDebounce(searchTerm, 300);

  // Reset ESPN state when search term changes
  useEffect(() => {
    setEspnResults([]);
    setEspnSearched(false);
  }, [debouncedSearch]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, selectedSport, teamFilter, claimStatusFilter, positionFilter, leagueFilter, conferenceFilter, divisionFilter, collegiateFilter, activeFilter, sortBy]);

  // Reset dependent filters when sport changes
  useEffect(() => {
    setTeamFilter('all');
    setPositionFilter('all');
    setLeagueFilter('all');
    setConferenceFilter('all');
    setDivisionFilter('all');
  }, [selectedSport]);

  // Fetch unique teams for filter dropdown
  const { data: teams } = useQuery({
    queryKey: ['espn-athlete-teams', selectedSport],
    queryFn: async () => {
      let query = supabase
        .from('espn_athletes')
        .select('current_team_name');

      if (selectedSport !== 'all') {
        query = query.eq('sport', selectedSport);
      }

      const { data, error } = await query;
      if (error) throw error;
      
      const uniqueTeams = [...new Set((data || [])
        .map(a => a.current_team_name)
        .filter(Boolean))]
        .sort() as string[];
      
      return uniqueTeams;
    },
  });

  // Fetch unique positions for filter dropdown
  const { data: positions } = useQuery({
    queryKey: ['espn-athlete-positions', selectedSport],
    queryFn: async () => {
      let query = supabase.from('espn_athletes').select('position');
      if (selectedSport !== 'all') query = query.eq('sport', selectedSport);
      const { data, error } = await query;
      if (error) throw error;
      return [...new Set((data || []).map(a => a.position).filter(Boolean))].sort() as string[];
    },
  });

  // Fetch unique leagues for filter dropdown
  const { data: leagues } = useQuery({
    queryKey: ['espn-athlete-leagues', selectedSport],
    queryFn: async () => {
      let query = supabase.from('espn_athletes').select('league');
      if (selectedSport !== 'all') query = query.eq('sport', selectedSport);
      const { data, error } = await query;
      if (error) throw error;
      return [...new Set((data || []).map(a => a.league).filter(Boolean))].sort() as string[];
    },
  });

  // Fetch unique conferences for filter dropdown
  const { data: conferences } = useQuery({
    queryKey: ['espn-athlete-conferences', selectedSport],
    queryFn: async () => {
      let query = supabase.from('espn_athletes').select('conference').eq('is_collegiate', true);
      if (selectedSport !== 'all') query = query.eq('sport', selectedSport);
      const { data, error } = await query;
      if (error) throw error;
      return [...new Set((data || []).map(a => a.conference).filter(Boolean))].sort() as string[];
    },
  });

  // Fetch unique divisions for filter dropdown
  const { data: divisions } = useQuery({
    queryKey: ['espn-athlete-divisions', selectedSport],
    queryFn: async () => {
      let query = supabase.from('espn_athletes').select('division');
      if (selectedSport !== 'all') query = query.eq('sport', selectedSport);
      const { data, error } = await query;
      if (error) throw error;
      return [...new Set((data || []).map(a => a.division).filter(Boolean))].sort() as string[];
    },
  });

  // Fetch ESPN athletes with pagination
  // When the user is actively searching we fetch ALL matching rows (up to 500)
  // so we can sort them by relevance client-side, then paginate that sorted
  // list.  Without a search term we use the normal server-side pagination.
  const { data: athleteResult, isLoading: searchLoading } = useQuery({
    queryKey: ['espn-athletes-claim', selectedSport, debouncedSearch, teamFilter, claimStatusFilter, positionFilter, leagueFilter, conferenceFilter, divisionFilter, collegiateFilter, activeFilter, sortBy, currentPage],
    queryFn: async () => {
      const isSearching = !!debouncedSearch;

      let sortColumn = 'name';
      let ascending = true;
      switch (sortBy) {
        case 'name-desc': sortColumn = 'name'; ascending = false; break;
        case 'team': sortColumn = 'current_team_name'; ascending = true; break;
        case 'sport': sortColumn = 'sport'; ascending = true; break;
        default: sortColumn = 'name'; ascending = true;
      }

      let query = supabase
        .from('espn_athletes')
        .select('*', { count: 'exact' })
        .order(sortColumn, { ascending });

      if (isSearching) {
        // Fetch all matches so we can re-sort by relevance client-side
        query = query.limit(500);
      } else {
        // Normal server-side pagination when browsing
        const from = (currentPage - 1) * PAGE_SIZE;
        const to = from + PAGE_SIZE - 1;
        query = query.range(from, to);
      }

      if (selectedSport !== 'all') query = query.eq('sport', selectedSport);
      if (teamFilter !== 'all') query = query.eq('current_team_name', teamFilter);
      if (claimStatusFilter !== 'all') query = query.eq('claim_status', claimStatusFilter);
      if (positionFilter !== 'all') query = query.eq('position', positionFilter);
      if (leagueFilter !== 'all') query = query.eq('league', leagueFilter);
      if (conferenceFilter !== 'all') query = query.eq('conference', conferenceFilter);
      if (divisionFilter !== 'all') query = query.eq('division', divisionFilter);
      if (collegiateFilter === 'college') query = query.eq('is_collegiate', true);
      if (collegiateFilter === 'pro') query = query.eq('is_collegiate', false);
      // When searching by name, skip server-side active/retired filter so we can use
      // the more accurate api_data JSON status client-side (handles retired players with stale team names)
      if (!isSearching) {
        if (activeFilter === 'active') query = query.not('current_team_name', 'is', null);
        if (activeFilter === 'retired') query = query.is('current_team_name', null);
      }
      // ── Word-level matching ──────────────────────────────────────────
      // Split the search into individual words and require each word to
      // appear somewhere in the name.  This means "steph curry" matches
      // "Stephen Curry" (because "steph" ⊂ "Stephen" and "curry" ⊂ "Curry")
      // which a single `ilike '%steph curry%'` would miss.
      if (isSearching) {
        const searchWords = debouncedSearch.trim().split(/\s+/).filter(Boolean);
        for (const word of searchWords) {
          query = query.ilike('name', `%${word}%`);
        }
      }

      const { data, error, count } = await query;
      if (error) throw error;

      let athletes = data || [];

      // Client-side active/retired filtering using api_data for accuracy
      // This catches retired players whose current_team_name wasn't cleared in the DB
      if (activeFilter === 'active') {
        athletes = athletes.filter(a => getAthleteActiveStatus(a) === 'active');
      } else if (activeFilter === 'retired') {
        athletes = athletes.filter(a => getAthleteActiveStatus(a) === 'retired');
      }

      // ── Relevance sort ──────────────────────────────────────────────
      // When the user is searching, sort results by how closely the name
      // matches the query so the best match appears first regardless of
      // whether the athlete is active or retired.
      if (isSearching) {
        athletes.sort((a, b) => {
          const scoreA = computeRelevanceScore(a.name || '', debouncedSearch);
          const scoreB = computeRelevanceScore(b.name || '', debouncedSearch);
          if (scoreB !== scoreA) return scoreB - scoreA; // higher score first
          // Tie-break: alphabetical
          return (a.name || '').localeCompare(b.name || '');
        });
      }

      // ── Client-side pagination for search results ───────────────────
      const totalCount = isSearching ? athletes.length : (activeFilter !== 'all' ? athletes.length : (count || 0));
      if (isSearching) {
        const from = (currentPage - 1) * PAGE_SIZE;
        athletes = athletes.slice(from, from + PAGE_SIZE);
      }

      return { athletes, totalCount };
    },
    enabled: true,
  });

  const athletes = athleteResult?.athletes || [];
  const totalCount = athleteResult?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // ── Filter ESPN fallback results client-side ──────────────────────
  // ESPN results live outside the Supabase query, so we apply the same
  // user-selected filters here so toggling Sport / Status / Team / etc.
  // actually narrows the ESPN cards the user sees.
  const filteredEspnResults = React.useMemo(() => {
    let results = espnResults;

    // Sport filter  (espnResult.sportKey uses the same keys as SPORTS config: nba, nfl, …)
    if (selectedSport !== 'all') {
      results = results.filter(r => {
        const rSport = (r.sportKey || r.sport || '').toLowerCase();
        return rSport === selectedSport;
      });
    }

    // Active / Retired filter
    if (activeFilter === 'active') {
      results = results.filter(r => !r.status || r.status === 'Active');
    } else if (activeFilter === 'retired') {
      results = results.filter(r => r.status === 'Retired' || r.status === 'Inactive');
    }

    // Claim status filter — ESPN results are always unclaimed (not yet
    // imported into the local DB), so only the 'unclaimed' filter value
    // should let them through.
    if (claimStatusFilter !== 'all' && claimStatusFilter !== 'unclaimed') {
      results = []; // ESPN results can never be 'pending' or 'claimed'
    }

    // Team filter
    if (teamFilter !== 'all') {
      results = results.filter(r => r.team === teamFilter || r.current_team_name === teamFilter);
    }

    // Position filter
    if (positionFilter !== 'all') {
      results = results.filter(r => r.position === positionFilter);
    }

    // League filter
    if (leagueFilter !== 'all') {
      results = results.filter(r => {
        const rLeague = (r.league || '').toUpperCase();
        return rLeague === leagueFilter.toUpperCase();
      });
    }

    // Collegiate filter
    if (collegiateFilter === 'college') {
      results = results.filter(r => {
        const league = (r.league || '').toUpperCase();
        return ['NCAAF', 'NCAAM', 'NCAAW'].includes(league);
      });
    } else if (collegiateFilter === 'pro') {
      results = results.filter(r => {
        const league = (r.league || '').toUpperCase();
        return !['NCAAF', 'NCAAM', 'NCAAW'].includes(league);
      });
    }

    return results;
  }, [espnResults, selectedSport, activeFilter, claimStatusFilter, teamFilter, positionFilter, leagueFilter, collegiateFilter]);

  // Auto-trigger ESPN search when local DB returns 0 results
  useEffect(() => {
    if (!searchLoading && athletes.length === 0 && debouncedSearch.trim().length >= 2 && !espnSearching && !espnSearched) {
      handleESPNSearch();
    }
  }, [athletes.length, debouncedSearch, searchLoading, espnSearching, espnSearched]);

  // Fetch user's claim requests
  const { data: claimRequests, isLoading: claimsLoading } = useQuery({
    queryKey: ['my-claim-requests', currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return [];
      
      const { data, error } = await supabase
        .from('api_athlete_claim_requests')
        .select(`
          *,
          api_athletes (
            id,
            name,
            sport,
            photo_url,
            current_team_name,
            claim_status
          )
        `)
        .eq('profile_id', currentUser.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!currentUser?.id,
  });

  // Submit claim mutation
  const submitClaim = useMutation({
    mutationFn: async ({ athleteId, notes, proofUrl }: { 
      athleteId: string; 
      notes: string; 
      proofUrl?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke('claim-athlete-profile', {
        body: {
          apiAthleteId: athleteId,
          source: 'espn',
          verificationNotes: notes,
          verificationProofUrl: proofUrl || undefined,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: 'Claim Submitted',
        description: 'Your claim request has been submitted for admin review.',
      });
      queryClient.invalidateQueries({ queryKey: ['my-claim-requests'] });
      queryClient.invalidateQueries({ queryKey: ['espn-athletes-claim'] });
      setShowClaimDialog(false);
      setSelectedAthlete(null);
      setVerificationNotes('');
      setVerificationProofUrl('');
    },
    onError: (error: any) => {
      // Extract the actual error message from edge function response
      let errorMsg = 'Failed to submit claim request';
      if (error?.context?.body) {
        try {
          const body = typeof error.context.body === 'string' ? JSON.parse(error.context.body) : error.context.body;
          errorMsg = body?.error || errorMsg;
        } catch { /* ignore */ }
      } else if (error?.message) {
        errorMsg = error.message;
      }
      toast({
        title: 'Claim Failed',
        description: errorMsg,
        variant: 'destructive',
      });
    },
  });

  // ESPN search fallback - client-side to bypass server IP blocks
  const handleESPNSearch = async (overrideQuery?: string) => {
    const query = overrideQuery || debouncedSearch;
    if (!query.trim()) return;
    setEspnSearching(true);
    setEspnSearched(true);
    try {
      const { searchESPNAthletes } = await import('@/utils/espnSearch');
      const results = await searchESPNAthletes(query, selectedSport !== 'all' ? selectedSport : undefined);
      // Filter out athletes already in local marketplace
      const localEspnIds = new Set(athletes.map(a => a.espn_id));
      const filtered = results.filter(r => !localEspnIds.has(r.espnId));
      // Sort ESPN results by relevance too
      const searchQ = query.trim();
      filtered.sort((a, b) => {
        const scoreA = computeRelevanceScore(a.name || '', searchQ);
        const scoreB = computeRelevanceScore(b.name || '', searchQ);
        if (scoreB !== scoreA) return scoreB - scoreA;
        return (a.name || '').localeCompare(b.name || '');
      });
      setEspnResults(filtered);
    } catch (err: any) {
      toast({ title: 'ESPN Search Failed', description: err.message, variant: 'destructive' });
      setEspnResults([]);
    } finally {
      setEspnSearching(false);
    }
  };

  // Auto-detect ESPN URL or numeric ID paste and trigger search immediately
  const handleSearchInput = (value: string) => {
    setSearchTerm(value);
    // Detect ESPN URL or pure numeric ID
    const isESPNUrl = /espn\.com\/.*\/player\/_\/id\/\d+/.test(value);
    const isNumericId = /^\d{3,}$/.test(value.trim());
    if (isESPNUrl || isNumericId) {
      // Trigger ESPN search immediately with the pasted value
      setTimeout(() => handleESPNSearch(value.trim()), 0);
    }
  };

  // Sync ESPN athlete to local DB, then open claim dialog
  const handleSyncAndClaim = async (espnAthlete: any) => {
    const espnId = espnAthlete.espnId || espnAthlete.espn_id || espnAthlete.id;
    setSyncingAthleteId(String(espnId));
    try {
      const sportKey = espnAthlete.sportKey || espnAthlete.sport || selectedSport || 'basketball';
      
      const { data, error } = await supabase.functions.invoke('sync-espn-athletes', {
        body: { 
          sport: sportKey, 
          athleteId: Number(espnId),
          fallbackData: {
            name: espnAthlete.displayName || espnAthlete.name || espnAthlete.display_name,
            firstName: espnAthlete.firstName || espnAthlete.first_name,
            lastName: espnAthlete.lastName || espnAthlete.last_name,
            team: espnAthlete.team || espnAthlete.current_team_name,
            position: espnAthlete.position,
            headshot: espnAthlete.headshot || espnAthlete.headshot_url,
            league: espnAthlete.league,
          }
        },
      });
      if (error) throw error;

      const { data: localAthlete, error: fetchErr } = await supabase
        .from('espn_athletes')
        .select('*')
        .eq('espn_id', Number(espnId))
        .maybeSingle();

      if (fetchErr) throw fetchErr;
      if (!localAthlete) throw new Error('Athlete could not be synced. Please try again.');

      queryClient.invalidateQueries({ queryKey: ['espn-athletes-claim'] });
      setSelectedAthlete(localAthlete);
      setEspnResults([]);
      setEspnSearched(false);
      // Auto-open claim dialog if profile is unclaimed
      if (getEffectiveClaimStatus(localAthlete) === 'unclaimed') {
        setShowClaimDialog(true);
      }
    } catch (err: any) {
      toast({ title: 'Sync Failed', description: err.message, variant: 'destructive' });
    } finally {
      setSyncingAthleteId(null);
    }
  };

  // Cancel claim mutation — uses edge function with service role to reset all related tables
  const cancelClaim = useMutation({
    mutationFn: async ({ claimRequestId }: { claimRequestId: string }) => {
      const { data, error } = await supabase.functions.invoke('cancel-athlete-claim', {
        body: { claimRequestId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      toast({ title: 'Request Cancelled', description: 'Your claim request has been cancelled.' });
      queryClient.invalidateQueries({ queryKey: ['my-claim-requests'] });
      queryClient.invalidateQueries({ queryKey: ['espn-athletes-claim'] });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to cancel request', variant: 'destructive' });
    },
  });

  const handleClaimSubmit = () => {
    if (!selectedAthlete) return;
    if (!verificationNotes.trim()) {
      toast({
        title: 'Verification Notes Required',
        description: 'Please provide notes explaining why this is your profile',
        variant: 'destructive',
      });
      return;
    }

    submitClaim.mutate({
      athleteId: selectedAthlete.id,
      notes: verificationNotes,
      proofUrl: verificationProofUrl || undefined,
    });
  };

  const getStatusBadge = (status: string) => {
    const configs: Record<string, any> = {
      pending: { variant: 'secondary', icon: Clock, label: 'Pending Review' },
      approved: { variant: 'default', icon: CheckCircle, label: 'Approved' },
      rejected: { variant: 'destructive', icon: XCircle, label: 'Rejected' },
      cancelled: { variant: 'outline', icon: Ban, label: 'Cancelled' },
    };
    
    const config = configs[status] || configs.pending;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getAthleteDisplayName = (athlete: any) => {
    return athlete.display_name || athlete.name;
  };

  const getAthleteInitials = (athlete: any) => {
    const name = getAthleteDisplayName(athlete);
    return name?.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase() || '?';
  };

  // Map espn_athletes rows to the shape AthleteListView/CompactView expect
  const mappedAthletes = athletes.map((a: any) => {
    const status = getAthleteActiveStatus(a);
    return {
      id: a.id,
      name: a.name,
      display_name: a.display_name || a.name,
      first_name: a.first_name,
      sport: a.sport,
      league: a.sport,
      position: a.position,
      current_team_name: status === 'retired' ? 'Retired / Free Agent' : a.current_team_name,
      current_team_logo: status === 'retired' ? null : a.current_team_logo,
      headshot_url: a.headshot_url,
      age: a.age,
      height: a.height,
      weight: a.weight,
      jersey_number: a.jersey_number,
      college: a.college,
      claim_status: getEffectiveClaimStatus(a),
    };
  });

  const handleAthleteClick = (athlete: any) => {
    // Find the original athlete data to open the detail/claim dialog
    const original = athletes.find((a: any) => a.id === athlete.id);
    setSelectedAthlete(original || athlete);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const from = (currentPage - 1) * PAGE_SIZE + 1;
    const to = Math.min(currentPage * PAGE_SIZE, totalCount);

    const getPageNumbers = () => {
      const pages: (number | 'ellipsis')[] = [];
      if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        if (currentPage > 3) pages.push('ellipsis');
        for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
          pages.push(i);
        }
        if (currentPage < totalPages - 2) pages.push('ellipsis');
        pages.push(totalPages);
      }
      return pages;
    };

    return (
      <div className="flex flex-col items-center gap-3 mt-6">
        <p className="text-sm text-muted-foreground">
          Showing {from}-{to} of {totalCount} athletes
        </p>
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
            {getPageNumbers().map((page, i) =>
              page === 'ellipsis' ? (
                <PaginationItem key={`e-${i}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={page}>
                  <PaginationLink
                    isActive={currentPage === page}
                    onClick={() => setCurrentPage(page as number)}
                    className="cursor-pointer"
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              )
            )}
            <PaginationItem>
              <PaginationNext
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    );
  };

  if (!currentUser?.id) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Authentication Required</AlertTitle>
        <AlertDescription>Please log in to claim athlete profiles.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div>
        <Button variant="ghost" size="sm" className="mb-4 gap-2" onClick={() => navigate('/profile')}>
          <ArrowLeft className="h-4 w-4" />
          Back to Profile
        </Button>
        <div className="flex items-center gap-2 mb-2">
          <Shield className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">Claim Your Athlete Profile</h1>
        </div>
        <p className="text-muted-foreground">
          Search for your athlete profile from the Talent Marketplace and submit a claim request
        </p>
      </div>

      <Tabs defaultValue="search" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="search">Search Athletes</TabsTrigger>
          <TabsTrigger value="requests">
            My Requests
            {claimRequests && claimRequests.length > 0 && (
              <Badge variant="secondary" className="ml-2">{claimRequests.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Search Tab */}
        <TabsContent value="search" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Search Athletes</CardTitle>
              <CardDescription>
                Find your profile from the Talent Marketplace
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filter Bar */}
              <div className="flex flex-col gap-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                      placeholder="Search by name..."
                      value={searchTerm}
                      onChange={(e) => handleSearchInput(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  {debouncedSearch.trim() && !searchLoading && athletes.length > 0 && (
                    <div className="flex justify-center md:col-span-2">
                      <Button variant="outline" size="sm" className="text-accent border-accent hover:bg-accent/10" onClick={() => handleESPNSearch()} disabled={espnSearching}>
                        {espnSearching ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                        Can't find your athlete? Search database for additional athletes
                      </Button>
                    </div>
                  )}
                  <Select value={selectedSport} onValueChange={setSelectedSport}>
                    <SelectTrigger className="w-full md:w-[160px]">
                      <SelectValue placeholder="Sport" />
                    </SelectTrigger>
                    <SelectContent>
                      {SPORTS.map(sport => (
                        <SelectItem key={sport.value} value={sport.value}>
                          {sport.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  {/* Team Filter */}
                  <Select value={teamFilter} onValueChange={setTeamFilter}>
                    <SelectTrigger className="w-[160px]">
                      <SelectValue placeholder="All Teams" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Teams</SelectItem>
                      {(teams || []).map(team => (
                        <SelectItem key={team} value={team}>{team}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Position Filter */}
                  {(positions || []).length > 0 && (
                    <Select value={positionFilter} onValueChange={setPositionFilter}>
                      <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="Position" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Positions</SelectItem>
                        {(positions || []).map(pos => (
                          <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {/* League Filter */}
                  {(leagues || []).length > 0 && (
                    <Select value={leagueFilter} onValueChange={setLeagueFilter}>
                      <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="League" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Leagues</SelectItem>
                        {(leagues || []).map(lg => (
                          <SelectItem key={lg} value={lg}>{lg}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {/* Conference Filter */}
                  {(conferences || []).length > 0 && (
                    <Select value={conferenceFilter} onValueChange={setConferenceFilter}>
                      <SelectTrigger className="w-[170px]">
                        <SelectValue placeholder="Conference" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Conferences</SelectItem>
                        {(conferences || []).map(conf => (
                          <SelectItem key={conf} value={conf}>{conf}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {/* Division Filter */}
                  {(divisions || []).length > 0 && (
                    <Select value={divisionFilter} onValueChange={setDivisionFilter}>
                      <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="Division" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Divisions</SelectItem>
                        {(divisions || []).map(div => (
                          <SelectItem key={div} value={div}>{div}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {/* Collegiate Filter */}
                  <Select value={collegiateFilter} onValueChange={setCollegiateFilter}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Levels</SelectItem>
                      <SelectItem value="pro">Professional</SelectItem>
                      <SelectItem value="college">College</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Active/Retired Filter */}
                  <Select value={activeFilter} onValueChange={setActiveFilter}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="retired">Retired / FA</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Claim Status Filter */}
                  <Select value={claimStatusFilter} onValueChange={setClaimStatusFilter}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="Claim Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="unclaimed">Unclaimed</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="claimed">Claimed</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Sort */}
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Sort" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="name-asc">Name A-Z</SelectItem>
                      <SelectItem value="name-desc">Name Z-A</SelectItem>
                      <SelectItem value="team">Team</SelectItem>
                      <SelectItem value="sport">Sport</SelectItem>
                    </SelectContent>
                  </Select>

                  {(athletes.length > 0 || filteredEspnResults.length > 0) && (
                    <div className="ml-auto">
                      <ViewToggle view={viewType} onViewChange={setViewType} />
                    </div>
                  )}
                </div>
              </div>

              {searchLoading ? (
                <div className="text-center py-8">
                  <LoadingFallback />
                </div>
              ) : athletes.length > 0 ? (
                <>
                  {viewType === 'grid' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {athletes.map((athlete: any) => (
                        <Card 
                          key={athlete.id} 
                          className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                          onClick={() => setSelectedAthlete(athlete)}
                        >
                          <CardContent className="p-0">
                            <div className="relative">
                              <Avatar className="w-full h-40 rounded-none">
                                <AvatarImage src={athlete.headshot_url || undefined} className="object-cover" />
                                <AvatarFallback className="rounded-none text-2xl">
                                  {getAthleteInitials(athlete)}
                                </AvatarFallback>
                              </Avatar>
                              {athlete.is_verified && (
                                <Badge className="absolute top-2 right-2 bg-primary/90 backdrop-blur-sm">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Verified
                                </Badge>
                              )}
                              {(() => {
                                const athleteStatus = getAthleteActiveStatus(athlete);
                                if (athleteStatus === 'retired') {
                                  return (
                                    <div className="absolute bottom-2 left-2 bg-background/90 backdrop-blur-sm px-2 py-1 rounded-md">
                                      <span className="text-xs font-medium text-muted-foreground">Retired / Free Agent</span>
                                    </div>
                                  );
                                }
                                if (athlete.current_team_name) {
                                  return (
                                    <div className="absolute bottom-2 left-2 bg-background/90 backdrop-blur-sm px-2 py-1 rounded-md">
                                      {athlete.current_team_logo && (
                                        <img
                                          src={athlete.current_team_logo}
                                          alt={athlete.current_team_name}
                                          className="h-5 w-auto inline-block mr-2"
                                        />
                                      )}
                                      <span className="text-xs font-medium">{athlete.current_team_name}</span>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                            <div className="p-4">
                              <h3 className="font-semibold text-lg">{getAthleteDisplayName(athlete)}</h3>
                              <p className="text-sm text-muted-foreground capitalize">
                                {athlete.sport}
                                {athlete.position && ` • ${athlete.position}`}
                              </p>
                              {athlete.college && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {athlete.college}
                                </p>
                              )}
                              <Badge variant="outline" className="mt-2 capitalize">
                                {getEffectiveClaimStatus(athlete)}
                              </Badge>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}

                  {viewType === 'list' && (
                    <AthleteListView
                      athletes={mappedAthletes}
                      onAthleteClick={handleAthleteClick}
                    />
                  )}

                  {viewType === 'compact' && (
                    <AthleteCompactView
                      athletes={mappedAthletes}
                      onAthleteClick={handleAthleteClick}
                    />
                  )}

                  {renderPagination()}

                  {/* ESPN Database Results (when local results exist) */}
                  {espnSearched && (
                    <div className="mt-8 border-t border-border pt-6 space-y-4">
                      <h3 className="text-lg font-semibold">ESPN Database Results</h3>
                      {espnSearching && (
                        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-8">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Searching ESPN database...
                        </div>
                      )}
                      {!espnSearching && espnResults.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">No additional athletes found in ESPN database.</p>
                      )}
                      {!espnSearching && espnResults.length > 0 && filteredEspnResults.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">No ESPN results match your current filters.</p>
                      )}
                      {!espnSearching && filteredEspnResults.length > 0 && (
                        <div>
                          {viewType === 'grid' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {filteredEspnResults.map((athlete: any) => {
                                const espnId = String(athlete.espnId || athlete.espn_id || athlete.id);
                                const isActive = !athlete.status || athlete.status === 'Active';
                                const isExpanded = expandedEspnId === espnId;
                                return (
                                  <Card 
                                    key={espnId} 
                                    className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                                    onClick={() => setExpandedEspnId(prev => prev === espnId ? null : espnId)}
                                  >
                                    <CardContent className="p-0">
                                      <div className="relative">
                                        <Avatar className="w-full h-32 rounded-none">
                                          <AvatarImage src={athlete.headshot || athlete.headshot_url || athlete.photo_url} className="object-cover" />
                                          <AvatarFallback className="rounded-none text-xl">
                                            {(athlete.name || '??').split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                                          </AvatarFallback>
                                        </Avatar>
                                        <Badge 
                                          variant={isActive ? 'default' : 'secondary'} 
                                          className="absolute top-2 right-2"
                                        >
                                          {isActive ? 'Active' : 'Retired'}
                                        </Badge>
                                      </div>
                                      <div className="p-4 space-y-2">
                                        <h3 className="font-semibold">{athlete.name}</h3>
                                        <p className="text-sm text-muted-foreground capitalize">
                                          {athlete.sport}{athlete.position ? ` • ${athlete.position}` : ''}
                                        </p>
                                        {(athlete.team || athlete.current_team_name) && (
                                          <p className="text-xs text-muted-foreground">{athlete.team || athlete.current_team_name}</p>
                                        )}
                                        {isExpanded && (
                                          <div className="pt-2 border-t border-border space-y-1 text-sm text-muted-foreground">
                                            {athlete.league && <p><span className="font-medium text-foreground">League:</span> {athlete.league}</p>}
                                            {athlete.height && <p><span className="font-medium text-foreground">Height:</span> {athlete.height}</p>}
                                            {athlete.weight && <p><span className="font-medium text-foreground">Weight:</span> {athlete.weight}</p>}
                                            {athlete.age && <p><span className="font-medium text-foreground">Age:</span> {athlete.age}</p>}
                                            {athlete.college && <p><span className="font-medium text-foreground">College:</span> {athlete.college}</p>}
                                            {athlete.jersey_number && <p><span className="font-medium text-foreground">Jersey:</span> #{athlete.jersey_number}</p>}
                                            {athlete.status && <p><span className="font-medium text-foreground">Status:</span> {athlete.status}</p>}
                                          </div>
                                        )}
                                        <p className="text-xs text-muted-foreground">
                                          {isExpanded ? 'Click to collapse' : 'Click for details'}
                                        </p>
                                        <Button
                                          size="sm"
                                          className="w-full mt-2"
                                          disabled={syncingAthleteId === espnId}
                                          onClick={(e) => { e.stopPropagation(); handleSyncAndClaim(athlete); }}
                                        >
                                          {syncingAthleteId === espnId ? (
                                            <><Loader2 className="h-3 w-3 animate-spin mr-2" /> Importing...</>
                                          ) : (
                                            'Import & Claim'
                                          )}
                                        </Button>
                                      </div>
                                    </CardContent>
                                  </Card>
                                );
                              })}
                            </div>
                          )}

                          {viewType === 'list' && (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="w-[60px]"></TableHead>
                                  <TableHead>Name</TableHead>
                                  <TableHead>Sport / Position</TableHead>
                                  <TableHead>Team</TableHead>
                                  <TableHead>Status</TableHead>
                                  <TableHead className="w-[140px]"></TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {filteredEspnResults.map((athlete: any) => {
                                  const espnId = String(athlete.espnId || athlete.espn_id || athlete.id);
                                  const isActive = !athlete.status || athlete.status === 'Active';
                                  return (
                                    <TableRow key={espnId}>
                                      <TableCell>
                                        <Avatar className="h-10 w-10">
                                          <AvatarImage src={athlete.headshot || athlete.headshot_url || athlete.photo_url} className="object-cover" />
                                          <AvatarFallback className="text-xs font-bold">
                                            {(athlete.name || '??').split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                                          </AvatarFallback>
                                        </Avatar>
                                      </TableCell>
                                      <TableCell className="font-medium">{athlete.name}</TableCell>
                                      <TableCell className="text-muted-foreground capitalize">
                                        {athlete.sport}{athlete.position ? ` • ${athlete.position}` : ''}
                                      </TableCell>
                                      <TableCell className="text-sm">{athlete.team || athlete.current_team_name || '—'}</TableCell>
                                      <TableCell>
                                        <Badge variant={isActive ? 'default' : 'secondary'}>
                                          {isActive ? 'Active' : 'Retired'}
                                        </Badge>
                                      </TableCell>
                                      <TableCell>
                                        <Button
                                          size="sm"
                                          disabled={syncingAthleteId === espnId}
                                          onClick={() => handleSyncAndClaim(athlete)}
                                        >
                                          {syncingAthleteId === espnId ? (
                                            <><Loader2 className="h-3 w-3 animate-spin mr-2" /> Importing...</>
                                          ) : (
                                            'Import & Claim'
                                          )}
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          )}

                          {viewType === 'compact' && (
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-4">
                              {filteredEspnResults.map((athlete: any) => {
                                const espnId = String(athlete.espnId || athlete.espn_id || athlete.id);
                                const isActive = !athlete.status || athlete.status === 'Active';
                                return (
                                  <div
                                    key={espnId}
                                    className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors group relative"
                                    onClick={() => handleSyncAndClaim(athlete)}
                                  >
                                    {syncingAthleteId === espnId && (
                                      <div className="absolute inset-0 flex items-center justify-center bg-background/60 rounded-lg z-10">
                                        <Loader2 className="h-5 w-5 animate-spin text-primary" />
                                      </div>
                                    )}
                                    <div className="relative">
                                      <Avatar className="h-16 w-16">
                                        <AvatarImage src={athlete.headshot || athlete.headshot_url || athlete.photo_url} className="object-cover" />
                                        <AvatarFallback className="text-lg font-bold">
                                          {(athlete.name || '??').split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                                        </AvatarFallback>
                                      </Avatar>
                                      <Badge 
                                        variant={isActive ? 'default' : 'secondary'} 
                                        className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[10px] px-1.5 py-0"
                                      >
                                        {isActive ? 'Active' : 'Retired'}
                                      </Badge>
                                    </div>
                                    <div className="text-center min-w-0 w-full">
                                      <p className="text-sm font-medium truncate">{athlete.name}</p>
                                      <p className="text-xs text-muted-foreground truncate capitalize">{athlete.sport}</p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                </>
              ) : (
                <div className="text-center py-12 space-y-4">
                  <p className="text-muted-foreground">
                    {searchTerm || selectedSport !== 'all' || teamFilter !== 'all' || claimStatusFilter !== 'all' || activeFilter !== 'all' || positionFilter !== 'all' || leagueFilter !== 'all' || conferenceFilter !== 'all' || divisionFilter !== 'all' || collegiateFilter !== 'all'
                      ? activeFilter === 'retired' && !searchTerm
                        ? 'No retired athletes in the local database. Search by name to find retired players via ESPN.'
                        : 'No athletes found matching your filters.'
                      : 'Start searching to find your athlete profile'
                    }
                  </p>
                  {debouncedSearch.trim() && (
                    <div className="space-y-3">
                      {espnSearching && (
                        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Searching ESPN database...
                        </div>
                      )}

                      {espnSearched && !espnSearching && espnResults.length === 0 && (
                        <p className="text-sm text-muted-foreground">No results found on ESPN either. Try a different name.</p>
                      )}

                      {espnSearched && !espnSearching && espnResults.length > 0 && filteredEspnResults.length === 0 && (
                        <p className="text-sm text-muted-foreground">No ESPN results match your current filters.</p>
                      )}

                       {filteredEspnResults.length > 0 && (
                        <div className="mt-4 text-left">
                          {viewType === 'grid' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {filteredEspnResults.map((athlete: any) => {
                                const espnId = String(athlete.espnId || athlete.espn_id || athlete.id);
                                const isActive = !athlete.status || athlete.status === 'Active';
                                const isExpanded = expandedEspnId === espnId;
                                return (
                                  <Card 
                                    key={espnId} 
                                    className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                                    onClick={() => setExpandedEspnId(prev => prev === espnId ? null : espnId)}
                                  >
                                    <CardContent className="p-0">
                                      <div className="relative">
                                        <Avatar className="w-full h-32 rounded-none">
                                          <AvatarImage src={athlete.headshot || athlete.headshot_url || athlete.photo_url} className="object-cover" />
                                          <AvatarFallback className="rounded-none text-xl">
                                            {(athlete.name || '??').split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                                          </AvatarFallback>
                                        </Avatar>
                                        <Badge 
                                          variant={isActive ? 'default' : 'secondary'} 
                                          className="absolute top-2 right-2"
                                        >
                                          {isActive ? 'Active' : 'Retired'}
                                        </Badge>
                                      </div>
                                      <div className="p-4 space-y-2">
                                        <h3 className="font-semibold">{athlete.name}</h3>
                                        <p className="text-sm text-muted-foreground capitalize">
                                          {athlete.sport}{athlete.position ? ` • ${athlete.position}` : ''}
                                        </p>
                                        {(athlete.team || athlete.current_team_name) && (
                                          <p className="text-xs text-muted-foreground">{athlete.team || athlete.current_team_name}</p>
                                        )}
                                        {isExpanded && (
                                          <div className="pt-2 border-t border-border space-y-1 text-sm text-muted-foreground">
                                            {athlete.league && <p><span className="font-medium text-foreground">League:</span> {athlete.league}</p>}
                                            {athlete.height && <p><span className="font-medium text-foreground">Height:</span> {athlete.height}</p>}
                                            {athlete.weight && <p><span className="font-medium text-foreground">Weight:</span> {athlete.weight}</p>}
                                            {athlete.age && <p><span className="font-medium text-foreground">Age:</span> {athlete.age}</p>}
                                            {athlete.college && <p><span className="font-medium text-foreground">College:</span> {athlete.college}</p>}
                                            {athlete.jersey_number && <p><span className="font-medium text-foreground">Jersey:</span> #{athlete.jersey_number}</p>}
                                            {athlete.status && <p><span className="font-medium text-foreground">Status:</span> {athlete.status}</p>}
                                          </div>
                                        )}
                                        <p className="text-xs text-muted-foreground">
                                          {isExpanded ? 'Click to collapse' : 'Click for details'}
                                        </p>
                                        <Button
                                          size="sm"
                                          className="w-full mt-2"
                                          disabled={syncingAthleteId === espnId}
                                          onClick={(e) => { e.stopPropagation(); handleSyncAndClaim(athlete); }}
                                        >
                                          {syncingAthleteId === espnId ? (
                                            <><Loader2 className="h-3 w-3 animate-spin mr-2" /> Importing...</>
                                          ) : (
                                            'Import & Claim'
                                          )}
                                        </Button>
                                      </div>
                                    </CardContent>
                                  </Card>
                                );
                              })}
                            </div>
                          )}

                          {viewType === 'list' && (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="w-[60px]"></TableHead>
                                  <TableHead>Name</TableHead>
                                  <TableHead>Sport / Position</TableHead>
                                  <TableHead>Team</TableHead>
                                  <TableHead>Status</TableHead>
                                  <TableHead className="w-[140px]"></TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {filteredEspnResults.map((athlete: any) => {
                                  const espnId = String(athlete.espnId || athlete.espn_id || athlete.id);
                                  const isActive = !athlete.status || athlete.status === 'Active';
                                  return (
                                    <TableRow key={espnId}>
                                      <TableCell>
                                        <Avatar className="h-10 w-10">
                                          <AvatarImage src={athlete.headshot || athlete.headshot_url || athlete.photo_url} className="object-cover" />
                                          <AvatarFallback className="text-xs font-bold">
                                            {(athlete.name || '??').split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                                          </AvatarFallback>
                                        </Avatar>
                                      </TableCell>
                                      <TableCell className="font-medium">{athlete.name}</TableCell>
                                      <TableCell className="text-muted-foreground capitalize">
                                        {athlete.sport}{athlete.position ? ` • ${athlete.position}` : ''}
                                      </TableCell>
                                      <TableCell className="text-sm">{athlete.team || athlete.current_team_name || '—'}</TableCell>
                                      <TableCell>
                                        <Badge variant={isActive ? 'default' : 'secondary'}>
                                          {isActive ? 'Active' : 'Retired'}
                                        </Badge>
                                      </TableCell>
                                      <TableCell>
                                        <Button
                                          size="sm"
                                          disabled={syncingAthleteId === espnId}
                                          onClick={() => handleSyncAndClaim(athlete)}
                                        >
                                          {syncingAthleteId === espnId ? (
                                            <><Loader2 className="h-3 w-3 animate-spin mr-2" /> Importing...</>
                                          ) : (
                                            'Import & Claim'
                                          )}
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          )}

                          {viewType === 'compact' && (
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-4">
                              {filteredEspnResults.map((athlete: any) => {
                                const espnId = String(athlete.espnId || athlete.espn_id || athlete.id);
                                const isActive = !athlete.status || athlete.status === 'Active';
                                return (
                                  <div
                                    key={espnId}
                                    className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors group relative"
                                    onClick={() => handleSyncAndClaim(athlete)}
                                  >
                                    {syncingAthleteId === espnId && (
                                      <div className="absolute inset-0 flex items-center justify-center bg-background/60 rounded-lg z-10">
                                        <Loader2 className="h-5 w-5 animate-spin text-primary" />
                                      </div>
                                    )}
                                    <div className="relative">
                                      <Avatar className="h-16 w-16">
                                        <AvatarImage src={athlete.headshot || athlete.headshot_url || athlete.photo_url} className="object-cover" />
                                        <AvatarFallback className="text-lg font-bold">
                                          {(athlete.name || '??').split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                                        </AvatarFallback>
                                      </Avatar>
                                      <Badge 
                                        variant={isActive ? 'default' : 'secondary'} 
                                        className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[10px] px-1.5 py-0"
                                      >
                                        {isActive ? 'Active' : 'Retired'}
                                      </Badge>
                                    </div>
                                    <div className="text-center min-w-0 w-full">
                                      <p className="text-sm font-medium truncate">{athlete.name}</p>
                                      <p className="text-xs text-muted-foreground truncate capitalize">{athlete.sport}</p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Requests Tab */}
        <TabsContent value="requests" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>My Claim Requests</CardTitle>
              <CardDescription>Track the status of your submitted claim requests</CardDescription>
            </CardHeader>
            <CardContent>
              {claimsLoading ? (
                <div className="text-center py-8">
                  <LoadingFallback />
                </div>
              ) : claimRequests && claimRequests.length > 0 ? (
                <div className="space-y-4">
                  {claimRequests.map((request: any) => (
                    <Card key={request.id}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <Avatar className="h-16 w-16">
                            <AvatarImage src={request.api_athletes?.photo_url} />
                            <AvatarFallback>{request.api_athletes?.name?.[0]}</AvatarFallback>
                          </Avatar>
                          
                          <div className="flex-1 space-y-2">
                            <div className="flex items-start justify-between">
                              <div>
                                <h3 className="font-semibold">{request.api_athletes?.name}</h3>
                                <p className="text-sm text-muted-foreground capitalize">
                                  {request.api_athletes?.sport} • {request.api_athletes?.current_team_name}
                                </p>
                              </div>
                              {getStatusBadge(request.status)}
                            </div>

                            {request.verification_notes && (
                              <div className="bg-muted p-3 rounded text-sm">
                                <strong>Your Notes:</strong> {request.verification_notes}
                              </div>
                            )}

                            {request.status === 'rejected' && request.admin_notes && (
                              <Alert variant="destructive">
                                <AlertCircle className="h-4 w-4" />
                                <AlertTitle>Rejection Reason</AlertTitle>
                                <AlertDescription>{request.admin_notes}</AlertDescription>
                              </Alert>
                            )}

                            {request.status === 'approved' && (
                              <Alert>
                                <CheckCircle className="h-4 w-4" />
                                <AlertTitle>Approved!</AlertTitle>
                                <AlertDescription>
                                  Your claim has been approved. This athlete profile is now linked to your account.
                                </AlertDescription>
                              </Alert>
                            )}

                            <div className="flex items-center justify-between">
                              <p className="text-xs text-muted-foreground">
                                Submitted {new Date(request.created_at).toLocaleDateString()}
                              </p>
                              {request.status === 'pending' && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="destructive" size="sm" disabled={cancelClaim.isPending}>
                                      {cancelClaim.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Ban className="h-3 w-3 mr-1" />}
                                      Cancel Request
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Cancel Claim Request?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        This will cancel your claim request for {request.api_athletes?.name}. You can submit a new request later.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Keep Request</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => cancelClaim.mutate({ claimRequestId: request.id })} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                        Yes, Cancel
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  No claim requests yet. Search for your athlete profile to get started.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Athlete Preview & Claim Dialog */}
      <Dialog open={!!selectedAthlete} onOpenChange={() => setSelectedAthlete(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Athlete Profile</DialogTitle>
            <DialogDescription>
              Review the profile details before submitting your claim
            </DialogDescription>
          </DialogHeader>

          {selectedAthlete && (
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <Avatar className="h-24 w-24">
                  <AvatarImage src={selectedAthlete.headshot_url || undefined} />
                  <AvatarFallback className="text-2xl">{getAthleteInitials(selectedAthlete)}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-2xl font-bold">{getAthleteDisplayName(selectedAthlete)}</h2>
                      <p className="text-muted-foreground capitalize">
                        {selectedAthlete.sport}
                        {selectedAthlete.position && ` • ${selectedAthlete.position}`}
                      </p>
                    </div>
                    {selectedAthlete.is_verified && (
                      <Badge>
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Verified
                      </Badge>
                    )}
                  </div>
                  
                  <div className="mt-3 space-y-1 text-sm">
                    {selectedAthlete.college && (
                      <p><strong>College:</strong> {selectedAthlete.college}</p>
                    )}
                    {selectedAthlete.age && (
                      <p><strong>Age:</strong> {selectedAthlete.age}</p>
                    )}
                    {(() => {
                      const status = getAthleteActiveStatus(selectedAthlete);
                      if (status === 'retired') {
                        return (
                          <div className="flex items-center gap-2">
                            <strong>Status:</strong>
                            <Badge variant="secondary">Retired / Free Agent</Badge>
                          </div>
                        );
                      }
                      if (selectedAthlete.current_team_name) {
                        return (
                          <div className="flex items-center gap-2">
                            <strong>Team:</strong>
                            {selectedAthlete.current_team_logo && (
                              <img
                                src={selectedAthlete.current_team_logo}
                                alt={selectedAthlete.current_team_name}
                                className="h-4 w-auto"
                              />
                            )}
                            <span>{selectedAthlete.current_team_name}</span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    {selectedAthlete.jersey_number && (
                      <p><strong>Number:</strong> {selectedAthlete.jersey_number}</p>
                    )}
                    {selectedAthlete.height && (
                      <p><strong>Height:</strong> {selectedAthlete.height}</p>
                    )}
                    {selectedAthlete.weight && (
                      <p><strong>Weight:</strong> {selectedAthlete.weight}</p>
                    )}
                  </div>
                </div>
              </div>

              <Alert>
                <Trophy className="h-4 w-4" />
                <AlertTitle>Claim Status</AlertTitle>
                <AlertDescription className="capitalize">
                  This profile is currently <strong>{getEffectiveClaimStatus(selectedAthlete)}</strong>
                </AlertDescription>
              </Alert>

              {(getEffectiveClaimStatus(selectedAthlete) === 'unclaimed') && (
                <Button 
                  onClick={() => setShowClaimDialog(true)} 
                  className="w-full"
                  size="lg"
                >
                  Claim This Profile
                </Button>
              )}

              {selectedAthlete.claim_status === 'pending' && (
                <Alert variant="default">
                  <Clock className="h-4 w-4" />
                  <AlertTitle>Claim Pending</AlertTitle>
                  <AlertDescription>
                    This profile has a pending claim request under review
                  </AlertDescription>
                </Alert>
              )}

              {selectedAthlete.claim_status === 'approved' && (
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertTitle>Already Claimed</AlertTitle>
                  <AlertDescription>
                    This profile has been claimed by another user
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Claim Submission Dialog */}
      <Dialog open={showClaimDialog} onOpenChange={setShowClaimDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit Claim Request</DialogTitle>
            <DialogDescription>
              Provide verification details to prove this is your athlete profile
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="notes">
                Verification Notes *
              </Label>
              <Textarea
                id="notes"
                placeholder="Explain why this is your profile. Include details like team history, achievements, or other identifying information..."
                value={verificationNotes}
                onChange={(e) => setVerificationNotes(e.target.value)}
                rows={4}
                required
              />
              <p className="text-xs text-muted-foreground">
                Be specific to help admins verify your identity
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="proof">
                Verification Proof URL (Optional)
              </Label>
              <Input
                id="proof"
                type="url"
                placeholder="https://... (link to social media, official website, etc.)"
                value={verificationProofUrl}
                onChange={(e) => setVerificationProofUrl(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Link to your social media profile, official team page, or other proof
              </p>
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Admin Review Required</AlertTitle>
              <AlertDescription>
                Your claim will be reviewed by our admin team. This typically takes 1-3 business days.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowClaimDialog(false);
                setVerificationNotes('');
                setVerificationProofUrl('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleClaimSubmit}
              disabled={submitClaim.isPending || !verificationNotes.trim()}
            >
              {submitClaim.isPending ? 'Submitting...' : 'Submit Claim'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
